import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { tr } from '@util/tr/tr';

interface NotificationType {
  value: string;
  label: string;
}

@Component({
  selector: 'web-resend-dialog',
  templateUrl: './resend-dialog.component.html',
  styleUrls: ['./resend-dialog.component.scss']
})
export class ResendDialogComponent {
  form: FormGroup;
  emailChoice: 'booking' | 'alternative' = 'booking';
  notificationTypes: NotificationType[] = [
    { value: '/resend-scheduled', label: tr('Scheduled') },
    { value: '/resend-confirmed', label: tr('Confirmed') },
    { value: '/resend-completed', label: tr('Completed') },
    { value: '/resend-rescheduled', label: tr('Rescheduled') },
    { value: '/resend-revoked', label: tr('Revoked') }
  ];

  compareFn(c1: any, c2: any): boolean {
    if (c1 && c2) {
      if (c1.value && c2.value) return c1.value === c2.value;
      return c1 === c2;
    }
    return false;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dlg: MatDialogRef<ResendDialogComponent>,
    private fb: FormBuilder
  ) {
    const emailLog = data?.emailLog;
    this.form = this.fb.group({
      wineryId: [emailLog?.wineryId || data?.currentWineryId || '', Validators.required],
      experienceId: [emailLog?.experienceId || '', Validators.required],
      saleId: [emailLog?.saleId || '', Validators.required],
      forcedLanguage: [emailLog?.language || '', Validators.required],
      endpoint: ['', Validators.required]
    });
  }

  // UUID validator function
  private uuidValidator(control: any) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (control.value && !uuidRegex.test(control.value)) {
      return { invalidUuid: true };
    }
    return null;
  }

  onEmailChoiceChange() {
    this.updateEmailValidation();

    if (this.emailChoice === 'booking') {
      this.form.get('emailAddress')?.setValue('');
    }
  }

  private updateEmailValidation() {
    const emailControl = this.form.get('emailAddress');
    if (!emailControl) return;

    if (this.emailChoice === 'alternative') {
      emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl.clearValidators();
    }
    emailControl.updateValueAndValidity();
  }

  submitClicked() {
    if (this.form.valid) {
      const values = {
        ...this.data,
        ...this.form.value
      };

      if (this.emailChoice === 'booking') {
        values.emailAddress = this.data.emailAddress;
      }

      this.dlg.close(values);
    }
  }

  cancelClicked() {
    this.dlg.close();
  }

  getNotificationTypeName(option: NotificationType): string {
    if (typeof option === 'string') {
      const found = this.notificationTypes.find(nt => nt.value === option);
      return found?.label || '';
    }
    return option?.label || '';
  }

  getSelectedNotificationType(): NotificationType | null {
    const value = this.form?.get('endpoint')?.value;
    if (!value) return null;
    if (typeof value === 'string') {
      return this.notificationTypes.find(nt => nt.value === value) || null;
    }
    return value;
  }

  onSelectorChange(fieldId: string, value: NotificationType): void {
    const control = this.form?.get(fieldId);
    if (!control) return;
    const newValue = typeof value === 'object' && value?.value ? value.value : value;
    control.setValue(newValue);
  }

  selectEmailChoice(choice: 'booking' | 'alternative') {
    if (this.emailChoice === choice) return;

    this.emailChoice = choice;
    this.onEmailChoiceChange();
  }
}